import { Task } from "gantt-task-react"
import { IDropDownOptions } from "./interface"
import { usePapaParse } from "react-papaparse"
import { SCREENS_DETAILS } from "./StaticData"
import { parse } from "date-fns"
import { format } from "date-fns"
import {
  NEW_PIPELINE_CONFIG,
  NewPipeLineTabs,
  newPipeLineTypes,
} from "./NewPipelineConfig"

export const CreateDropDownOptions = (
  options: string[],
): IDropDownOptions[] => {
  return options?.map((str) => {
    return {
      value: str,
      label: str,
    }
  })
}

export const inputSeriesDateConversionBeforeSave = (configData: any) => {
  return configData.map((obj: any) => {
    const tempObj = { ...obj }
    const startDate = format(obj.model_refresh_start_date, "dd-MM-yyyy")
    const endDate = format(obj.model_refresh_end_date, "dd-MM-yyyy")
    tempObj.model_refresh_start_date = startDate
    tempObj.model_refresh_end_date = endDate
    return tempObj
  })
}

export const JsonToCsv = (data: any) => {
  const { jsonToCSV } = usePapaParse()
  const csv = jsonToCSV(data)
  return csv
}

export const downloadCSVFile = (csv: any, filename: string) => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
  const link = document.createElement("a")

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob)
    link.setAttribute("href", url)
    link.setAttribute("download", `${filename}.csv`)
    link.style.visibility = "hidden"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
}

export const GetDynamicUrl = (apiStr: string) => {
  return `${import.meta.env.VITE_APP_BASE_URL + apiStr}`
}

export const constructURL = (api: string, params: any = {}) => {
  const queryString = Object.keys(params)
    .map((key) => `${key}=${encodeURIComponent(params[key])}`)
    .join("&")

  if (queryString) {
    return `${import.meta.env.VITE_APP_BASE_URL + api}?${queryString}`
  } else {
    return import.meta.env.VITE_APP_BASE_URL + api
  }
}

export function getStartEndDateForProject(tasks: Task[], projectId: string) {
  const projectTasks = tasks.filter((t) => t.project === projectId)
  let start = projectTasks[0].start
  let end = projectTasks[0].end

  for (let i = 0; i < projectTasks.length; i++) {
    const task = projectTasks[i]
    if (start.getTime() > task.start.getTime()) {
      start = task.start
    }
    if (end.getTime() < task.end.getTime()) {
      end = task.end
    }
  }
  return [start, end]
}

export const ABBREVIATE_NUMBER = (num: number, fixed: number) => {
  const isNegative = num < 0
  num = Math.abs(num)

  if (num < 1000) {
    const formattedNum = num.toFixed(fixed)
    return isNegative ? `-${formattedNum}` : formattedNum
  }

  const suffixes = ["", "K", "M", "B", "T"]
  let index = 0

  while (num >= 1000 && index < suffixes.length - 1) {
    num /= 1000.0
    index++
  }

  const formattedNum = num.toFixed(fixed) + suffixes[index]
  return isNegative ? `-${formattedNum}` : formattedNum
}

export const getCurrentParamsInObj = (searchParams: any) => {
  const tempParams: any = {}
  for (const [key, value] of searchParams.entries()) {
    tempParams[key] = value
  }
  return tempParams
}

export const getUniqueValsFromArrayOfObjs = (key: string, list: any) => {
  return Array.from(new Set(list.map((obj: any) => obj[key])))
}

export const pipelineConfigMapping = (tableData: any, tabIndex: string) => {
  return tableData.map((obj: any) => {
    // NEW_PIPELINE_CONFIG[state.newPipeLine.tabIndex].fin
    const keys = Object.keys(obj)
    keys.forEach((key) => {
      const keyType = NEW_PIPELINE_CONFIG[tabIndex].find(
        (keyTypeObj: any) => keyTypeObj.key === key,
      )
      // if(keyType.type===newPipeLineTypes.inputWithDate){
      //   obj[key] =
      // }
      if (keyType.type === newPipeLineTypes.dropDownWithBools) {
        if (obj[key]) {
          obj[key] = "True"
        } else {
          obj[key] = "False"
        }
      }
      if (keyType.type === newPipeLineTypes.inputWithDate) {
        obj[key] = parse(obj[key], "dd-mm-yyyy", new Date())
      }
    })

    return obj
  })
}

export const findIndexByKey = (key: string) => {
  return NewPipeLineTabs.findIndex((obj) => obj.key === key)
}

export const sortByYearAndWeek = (
  arr: any,
  yearKey = "year",
  weekKey = "view",
) => {
  // Custom sorting function
  function compare(a: any, b: any) {
    // Compare years first
    if (a[yearKey] < b[yearKey]) return -1
    if (a[yearKey] > b[yearKey]) return 1

    // If years are equal, compare weeks
    if (a[weekKey] < b[weekKey]) return -1
    if (a[weekKey] > b[weekKey]) return 1

    return 0 // Objects are equal
  }

  // Sort the array using the custom compare function
  arr.sort(compare)

  return arr
}

export const sortByYearAndMonth = (
  arr: any,
  yearKey = "Refresh_Year",
  monthKey = "Refresh_Month",
) => {
  // Custom sorting function
  function compare(a: any, b: any) {
    // Compare years first
    if (a[yearKey] < b[yearKey]) return -1
    if (a[yearKey] > b[yearKey]) return 1

    // If years are equal, compare months
    if (a[monthKey] < b[monthKey]) return -1
    if (a[monthKey] > b[monthKey]) return 1

    return 0 // Objects are equal
  }

  // Sort the array using the custom compare function
  arr.sort(compare)

  return arr
}

export const sortByDate = (arr: any) => {
  return arr.sort(
    (a: any, b: any) =>
      Number(new Date(a.start_time__date)) -
      Number(new Date(b.start_time__date)),
  )
}

export const parseAndFormatDate = (
  date: string,
  existingFormat: string,
  requiredFormat: string,
): string => {
  const parsedDate = parse(date, existingFormat, new Date())
  return format(parsedDate, requiredFormat)
}

export const setCurrentScreenVal = (location: any, setCurrentScreen: any) => {
  if (location.pathname.includes(SCREENS_DETAILS.DATA_METADATA)) {
    setCurrentScreen(SCREENS_DETAILS.DATA_METADATA)
  } else if (location.pathname.includes(SCREENS_DETAILS.MONITORING)) {
    setCurrentScreen(SCREENS_DETAILS.MONITORING)
  } else if (location.pathname.includes(SCREENS_DETAILS.MODEL_META_DATA)) {
    setCurrentScreen(SCREENS_DETAILS.MODEL_META_DATA)
  } else if (location.pathname.includes(SCREENS_DETAILS.MAINTENANCE)) {
    setCurrentScreen(SCREENS_DETAILS.MAINTENANCE)
  }
}

export function formatDate(dateString: string) {
  const date = new Date(dateString)

  const formattedDate = date.toLocaleString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    // hour: '2-digit',
    // minute: '2-digit',
    // second: '2-digit'
  })

  return formattedDate
}

export const convertMonthYearParamsToDate = (params: any[]) => {
  // Keys to convert: anything ending in "_year_month"
  const datePattern = /^\d{6}$/

  return params.map((param) => {
    const { param_name, default_value } = param

    const shouldConvert =
      typeof default_value === "string" &&
      datePattern.test(default_value) &&
      param_name.toLowerCase().endsWith("_year_month")

    if (shouldConvert) {
      const year = parseInt(default_value.substring(0, 4), 10)
      const month = parseInt(default_value.substring(4, 6), 10) - 1 // JS month index starts at 0

      return {
        ...param,
        default_value: new Date(year, month, 1).toISOString(),
      }
    }

    return param
  })
}

export const flattenParamsToKeyValue = (params: any[]) => {
  return params.reduce((acc, param) => {
    const { param_name, default_value } = param

    const isDateString =
      typeof default_value === "string" && default_value.includes("T")
    const isYearMonthField = /year_month/i.test(param_name)

    if (isYearMonthField && isDateString) {
      const date = new Date(default_value)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, "0") // zero-padded
      acc[param_name] = `${year}${month}`
    } else {
      acc[param_name] = default_value
    }

    return acc
  }, {} as Record<string, string>)
}
